import { BACK_LINK } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { PageHero } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';

import { LegalLastUpdated } from './LegalLastUpdated';
import { LegalTranslationNotice } from './LegalTranslationNotice';
import { showsTranslationNotice } from './translation-notice-visibility';

import type { LegalDocSlug } from './legal-doc-dates';
import type { ContainerWidth } from '@/components/marketing-ui';
import type { ReactElement, ReactNode } from 'react';

/**
 * The shell every trust + legal page shares.
 *
 * It exists because twelve pages hand-repeated the same twelve lines of
 * masthead — breadcrumb link, `<h1>`, translation notice, intro, last-updated —
 * each with its own copy of the type scale. That is the drift the redesign
 * consolidated everywhere else: the pages were already identical in structure
 * and were only identical in APPEARANCE for as long as nobody edited one of
 * them. Now the masthead has one definition, and these pages get the same
 * brand-ruled `PageHero` the rest of the site does instead of a bare `<h1>` on
 * a blank page.
 *
 * The body is deliberately NOT abstracted: each document's sections are its
 * own, and a "legal section" component would be a shape imposed on twelve
 * documents that do not share one. What they share is the frame.
 *
 * Prose sizing lives here too (`text-[1.02rem] leading-[1.7]`), a step up from
 * the site's body copy. Legal text is read carefully and at length, so it earns
 * a larger size and looser leading than a marketing paragraph does.
 */
export interface LegalPageProps {
  /** Label for the "← Trust center" breadcrumb above the title. */
  readonly breadcrumb: ReactNode;
  readonly title: ReactNode;
  /** Lead paragraph. May carry inline links (several pages carry a mailto). */
  readonly intro?: ReactNode;
  /**
   * Slug for the `LegalLastUpdated` stamp. Omitted on the pages that carry no
   * dated instrument (the trust hub, the security overview) — those state a
   * posture rather than a version, so a date would imply a revision history
   * they do not have.
   */
  readonly lastUpdatedSlug?: LegalDocSlug;
  /** `prose` (default) for a document; `default` for a page built around a table. */
  readonly width?: ContainerWidth;
  readonly children: ReactNode;
}

export function LegalPage({
  breadcrumb,
  title,
  intro,
  lastUpdatedSlug,
  width = 'prose',
  children,
}: LegalPageProps): ReactElement {
  const { locale } = useT();
  // Both children of the meta row can render nothing — the notice self-hides on
  // English, and a page stating a posture rather than a version carries no
  // stamp. When neither will appear the row is an empty box holding 32px open,
  // which on `/trust/{accessibility,security,security-questionnaire,subprocessors}`
  // read as a gap somebody forgot to fill.
  const hasMeta = showsTranslationNotice(locale) || lastUpdatedSlug !== undefined;
  return (
    <main id="main" tabIndex={-1} className="bg-[color:var(--bd-page)]">
      <PageHero
        align="start"
        width={width}
        eyebrow={
          <Link className={BACK_LINK} to="/trust">
            {breadcrumb}
          </Link>
        }
        title={title}
        description={intro}
      />

      <div
        className={
          // `break-words` for the same reason the docs body carries it: legal prose
          // quotes addresses, URLs and instrument names that cannot wrap.
          `mx-auto break-words px-6 pb-20 pt-10 text-[1.02rem] leading-[1.7] text-[color:var(--bd-body)] ${
            width === 'prose' ? 'max-w-3xl' : 'max-w-5xl'
          }`
        }
      >
        {hasMeta ? (
          <div className="mb-8 flex flex-col gap-2">
            <LegalTranslationNotice />
            {lastUpdatedSlug !== undefined ? <LegalLastUpdated slug={lastUpdatedSlug} /> : null}
          </div>
        ) : null}
        {children}
      </div>
    </main>
  );
}
